import type { FeatureAccessKeys } from '../../api';
import type { FeatureFlags } from '../featureFlags';

/**
 * Base condition types for feature evaluation
 */
export type FeatureCondition =
	| FeatureFlagCondition
	| FeatureAccessCondition
	| NotCondition
	| AndCondition
	| OrCondition;

export interface FeatureFlagCondition {
	type: 'featureFlag';
	flag: keyof FeatureFlags;
}

export interface FeatureAccessCondition {
	type: 'featureAccess';
	access: FeatureAccessKeys;
}

export interface NotCondition {
	type: 'not';
	condition: FeatureCondition;
}

export interface AndCondition {
	type: 'and';
	conditions: FeatureCondition[];
}

export interface OrCondition {
	type: 'or';
	conditions: FeatureCondition[];
}

/**
 * Evaluates a complex feature condition against the current feature flags and access
 */
export function evaluateFeatureCondition(
	condition: FeatureCondition,
	featureFlags: Record<string, boolean>,
	featureAccess: Record<string, any>
): boolean {
	switch (condition.type) {
		case 'featureFlag':
			return Boolean(featureFlags[condition.flag]);

		case 'featureAccess':
			return Boolean(featureAccess[condition.access]);

		case 'not':
			return !evaluateFeatureCondition(
				condition.condition,
				featureFlags,
				featureAccess
			);

		case 'and':
			return condition.conditions.every((cond) =>
				evaluateFeatureCondition(cond, featureFlags, featureAccess)
			);

		case 'or':
			return condition.conditions.some((cond) =>
				evaluateFeatureCondition(cond, featureFlags, featureAccess)
			);

		default:
			return false;
	}
}

/**
 * Helper function to create a feature flag condition
 */
export function featureFlag(flag: keyof FeatureFlags): FeatureFlagCondition {
	return { type: 'featureFlag', flag };
}

/**
 * Helper function to create a feature access condition
 */
export function featureAccess(
	access: FeatureAccessKeys
): FeatureAccessCondition {
	return { type: 'featureAccess', access };
}

/**
 * Helper function to create a NOT condition
 */
export function not(condition: FeatureCondition): NotCondition {
	return { type: 'not', condition };
}

/**
 * Helper function to create an AND condition
 */
export function and(...conditions: FeatureCondition[]): AndCondition {
	return { type: 'and', conditions };
}

/**
 * Helper function to create an OR condition
 */
export function or(...conditions: FeatureCondition[]): OrCondition {
	return { type: 'or', conditions };
}
